import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

import {
  checkDevApiBinding,
  HUB_DEV_API_BINDING_ERROR_EVENT,
  type DevApiBindingIssue,
} from "@/lib/hubDevApiBinding";

/**
 * Dev-only banner when the browser UI port and VITE_API_ORIGIN disagree
 * (for example branch UI on 5176 still targeting primary 8123).
 */
export function HubDevApiBindingBanner() {
  const [issue, setIssue] = useState<DevApiBindingIssue | null>(() =>
    import.meta.env.DEV ? checkDevApiBinding() : null,
  );

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const onBindingError = (event: Event) => {
      const detail = (event as CustomEvent<DevApiBindingIssue>).detail;
      if (detail?.message) {
        setIssue(detail);
      }
    };

    window.addEventListener(HUB_DEV_API_BINDING_ERROR_EVENT, onBindingError);
    return () => {
      window.removeEventListener(HUB_DEV_API_BINDING_ERROR_EVENT, onBindingError);
    };
  }, []);

  if (!import.meta.env.DEV || issue === null) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed inset-x-0 top-0 z-[301] flex justify-center px-4 pt-3"
    >
      <div className="max-w-3xl rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-950 shadow-sm">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-700" aria-hidden="true" />
          <div>
            <p className="font-medium">Wrong dev API port</p>
            <p className="mt-1">{issue.message}</p>
            <p className="mt-1 font-mono text-xs text-red-800">
              UI port {issue.uiPort ?? "?"} → expected API :{issue.expectedApiPort}
              {issue.configuredOrigin ? ` (configured ${issue.configuredOrigin})` : ""}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
